import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCustomTypes } from "./useCustomTypes";
import { CustomField } from "../../../api/CustomFields/CustomFields";
import { Control, Path, UseFormGetValues } from "react-hook-form";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import {
  GenericEntity,
  ICustomTypedEntity,
  IGenericRequestParameters,
  InferSortByOptions,
  IUniqueEntity,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import { CustomType } from "../../../api/CustomTypes/CustomTypes";
import { NotAvailable, NotSet } from "../../../common/misc/UIconstants";
import { CustomFieldValueRenderer } from "../../CustomFields/CustomFieldRenderer";
import { MultiEditMappedItemObject } from "../../../common/forms/MultiEditForms/common/MultiEditProvider";
import { MultiEditHeaderWrapper } from "../../../common/forms/MultiEditForms/common/MultiEditFormWrapper";
import { CustomFieldEditRenderer } from "../../CustomFields/CustomFieldEditRenderer";
import { customFieldDataTypeToColumnWidth, customFieldToCustomFieldForm } from "../../CustomFields/CustomFieldUtils";
import { MultiEditCustomFieldForm } from "../../../common/forms/MultiEditForms/forms/MultiEditCustomFieldForm";
import { CustomFieldDataTypeUtils } from "../../../api/CustomFields/CustomFieldsBase";
import { CustomTypeEntityType } from "../../../api/CustomTypes/CustomTypesBase";

interface UseCustomTypesEntityTableProps<
  Entity extends GenericEntity & ICustomTypedEntity & IUniqueEntity,
  SortByOptions extends string,
> {
  defaultColumnSettings: ColumnsSettings<Entity>;
  entityType: CustomTypeEntityType;
  featureFlag?: boolean;
  customTypeIds?: number[];
  defaultActive?: boolean;
  sort: SortState<SortByOptions>[];
  setSort: (newSort: SortState<SortByOptions>[]) => Promise<void>;
}
export const useCustomTypesEntityTable = <
  Entity extends GenericEntity & ICustomTypedEntity & IUniqueEntity,
  SortByOptions extends string,
>({
  defaultColumnSettings,
  entityType,
  featureFlag = true,
  customTypeIds,
  defaultActive = false,
  sort,
  setSort,
}: UseCustomTypesEntityTableProps<Entity, SortByOptions>) => {
  const [customFieldHeaders, setCustomFieldHeaders] = useState<GenericVirtualizedTableCells<Entity>>([]);
  const [columnSettings, setColumnSettings] = useState<ColumnsSettings<Entity>>(defaultColumnSettings); // The columnSettings will be held here
  const { types } = useCustomTypes({ entityType: entityType, filters: { ids: customTypeIds } });

  useMemo(() => {
    if (types && featureFlag) {
      const def = { ...defaultColumnSettings }; // we make a copy of the default columns as we're going to modify them later
      if (Object.keys(types).length > 0) {
        // let _customSchemaColumnFieldsDefinition: CustomSchemaColumnFieldsDefinition = {};
        let last_position = Object.keys(defaultColumnSettings).length;

        // Collect all custom fields
        const customFields = Object.values(types)
          .map((s) => s.sections?.map((_s) => _s.customFields?.map((c) => c)))
          .flat(Infinity)
          .filter((c) => c) as CustomField[];

        const customFieldDict = Object.fromEntries(customFields.map((c) => [c.id, c]));

        Object.values(customFieldDict).forEach((field) => {
          def[field.id] = {
            pos: last_position,
            active: defaultActive,
            label: "Custom field",
            header: `${field.name}`,
            property: `customFields.${field.id}` as any,
          };
          last_position += 1;
        });

        let headers: GenericVirtualizedTableCells<Entity> = [];
        // Build custom field headers
        if (customFieldDict) {
          Object.entries(customFieldDict).forEach(([customFieldId, field]) => {
            const _customFieldId = +customFieldId;
            headers.push({
              Header: field.name,
              id: customFieldId,
              accessor: (row: Entity) =>
                row.customFields && Object.hasOwn(row.customFields, customFieldId) ? (
                  row.customFields?.[_customFieldId] !== undefined &&
                  row.customFields?.[_customFieldId] !== null &&
                  row.customFields?.[_customFieldId] !== "" ? (
                    <CustomFieldValueRenderer
                      customfield={field}
                      value={row.customFields?.[_customFieldId]}
                      title={""}
                    />
                  ) : (
                    NotSet
                  )
                ) : (
                  NotAvailable
                ),
              width: 150,
              align: "left",
              ...(canSortByCustomField(field.dataType) && {
                sortingFn: async (id, allowMultiSort) => {
                  const currentSortIndex =
                    Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
                  if (allowMultiSort) {
                    if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
                      if (sort[currentSortIndex].sortDirection === "ASC") {
                        const newSort = [...sort];
                        newSort[currentSortIndex] = {
                          headerId: id,
                          sortDirection: "DESC",
                          property: "CUSTOM_FIELD" as any,
                          customFieldId: field.id,
                        };
                        await setSort(newSort);
                      } else {
                        const newSort = [...sort];
                        newSort[currentSortIndex] = {
                          headerId: id,
                          sortDirection: "ASC",
                          property: "CUSTOM_FIELD" as any,
                          customFieldId: field.id,
                        };
                        await setSort(newSort);
                      }
                    } else {
                      await setSort([
                        ...sort,
                        {
                          headerId: id,
                          sortDirection: "ASC",
                          property: "CUSTOM_FIELD" as any,
                          customFieldId: field.id,
                        },
                      ]);
                    }
                  } else {
                    if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
                      if (sort[currentSortIndex].sortDirection === "ASC") {
                        await setSort([
                          {
                            headerId: id,
                            sortDirection: "DESC",
                            property: "CUSTOM_FIELD" as any,
                            customFieldId: field.id,
                          },
                        ]);
                      } else {
                        await setSort([
                          {
                            headerId: id,
                            sortDirection: "ASC",
                            property: "CUSTOM_FIELD" as any,
                            customFieldId: field.id,
                          },
                        ]);
                      }
                    } else {
                      await setSort([
                        {
                          headerId: id,
                          sortDirection: "ASC",
                          property: "CUSTOM_FIELD" as any,
                          customFieldId: field.id,
                        },
                      ]);
                    }
                  }
                },
                // sortDirection: (id) => (sort.headerId === id ? sort.sortDirection : undefined),
                sortDirection: (id) => {
                  const currentSortIndex =
                    Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
                  if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
                    return sort[currentSortIndex].sortDirection;
                  }
                  return undefined;
                },
                sortIndex: (id: string) => {
                  if (Array.isArray(sort) && !!sort.length) {
                    const currentSortIndex = sort.findIndex((s) => s.headerId === id);
                    return currentSortIndex >= 0 ? currentSortIndex + 1 : undefined;
                  }
                  return undefined;
                },
              }),
            });
          });
        }
        setCustomFieldHeaders(headers);
      }
      setColumnSettings(def);
    }
  }, [defaultActive, defaultColumnSettings, featureFlag, setSort, sort, types]);

  return { columnSettings, customFieldHeaders, types };
};

// For bulk edit

type CustomFieldEntry = CustomField & { customTypeIds: CustomType["id"][] };
type CustomFieldSingleEntry = CustomField & { customTypeId: CustomType["id"] };
interface MultiEditCustomFieldTableCellsProps<
  Entity extends GenericEntity & ICustomTypedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
> {
  types: MultiEditMappedItemObject<CustomType> | undefined;
  control: Control<Entity, any>;
  getValues: UseFormGetValues<Entity>;
  selection: Set<Entity["id"]>;
  disabled?: boolean;
  sort: SortState<InferSortByOptions<Filters>>[];
  setSort: React.Dispatch<React.SetStateAction<SortState<InferSortByOptions<Filters>>[]>>;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}

export const useMultiEditCustomFieldTableCells = <
  Entity extends GenericEntity & ICustomTypedEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
>({
  types,
  control,
  getValues,
  selection,
  disabled = false,
  sort,
  setSort,
  setFilters,
}: MultiEditCustomFieldTableCellsProps<Entity, Filters>) => {
  let res: GenericVirtualizedTableCells<Entity> = [];

  if (!types) return [];

  // Collect all custom fields
  const customFields = Object.values(types)
    .map((s) => s.sections?.map((_s) => _s.customFields?.map((c) => ({ ...c, customTypeId: s.id }))))
    .flat(Infinity)
    .filter((c) => c) as CustomFieldSingleEntry[];

  let customFieldDict: MultiEditMappedItemObject<CustomFieldEntry> = {};
  customFields.forEach((customField) => {
    if (CustomFieldDataTypeUtils.HasEditForm(customField.dataType)) {
      if (!Object.hasOwn(customFieldDict, customField.id)) {
        customFieldDict[customField.id] = {
          ...customField,
          customTypeIds: [customField.customTypeId],
        };
      } else {
        customFieldDict[customField.id] = {
          ...customField,
          customTypeIds: [...customFieldDict[customField.id].customTypeIds, customField.customTypeId],
        };
      }
    }
  });

  Object.values(customFieldDict).forEach((customField: CustomFieldEntry) => {
    res.push({
      id: `${customField.id}`,
      label: customField.name,
      Header: (
        <MultiEditHeaderWrapper<Entity>
          control={control}
          getValues={getValues}
          ids={selection}
          label={customField.name}
          property={`customFields.${customField.id}`}
          required={customField.required}
          customTypeIds={customField.customTypeIds}
          hasApply
          disabled={(consolidatedValues) => {
            return disabled;
          }}
          hasAppend={CustomFieldDataTypeUtils.IsArrayDataType(customField.dataType)}
        >
          {(consolidatedValues, consolidatedCustomFieldValues, customFieldPlaceholders) => {
            const _customField = customFieldToCustomFieldForm(customField);
            return (
              <CustomFieldEditRenderer<Entity>
                id={`customFields.${customField.id}` as Path<Entity>}
                label={_customField.name ?? ""}
                control={control}
                errors={{}}
                placeholder={customFieldPlaceholders?.[customField.id]}
                // required={_customField.required}
                defaultValue={_customField.defaultValues}
                disabled={disabled || _customField.readOnly}
                dataType={_customField.dataType!}
                predefinedOptions={_customField.predefinedOptions}
                showAsTextArea={_customField.showAsTextArea}
                description={_customField.description}
                isMulti={!!_customField.isMulti}
                customFieldId={customField.id}
                predefinedOptionsFromValues={customField.predefinedOptionsFromValues}
                filters={
                  Array.isArray(customField.customTypeConstraint) && !!customField.customTypeConstraint.length
                    ? { customTypeIds: customField.customTypeConstraint.map((c) => c.id) }
                    : { customTypeIds: null }
                }
                uncontained
              />
            );
          }}
        </MultiEditHeaderWrapper>
      ),
      accessor: (row) => (
        <div className="flex" style={{ overflow: "auto", width: "100%", height: "100%" }}>
          <MultiEditCustomFieldForm row={row} customField={customField} types={types} disabled={disabled} />
        </div>
      ),
      width: customFieldDataTypeToColumnWidth(customField.dataType),
      ...(canSortByCustomField(customField.dataType) && {
        sortingFn: async (id) => {
          const currentSortIndex =
            Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
          if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
            if (sort[currentSortIndex].sortDirection === "ASC") {
              const newSort = [...sort];
              newSort[currentSortIndex] = {
                headerId: id,
                sortDirection: "DESC",
                property: "CUSTOM_FIELD" as any,
                customFieldId: customField.id,
              };
              setSort(newSort);
              setFilters((prev) => ({
                ...prev,
                sortBy: newSort,
              }));
            } else {
              const newSort = [...sort];
              newSort[currentSortIndex] = {
                headerId: id,
                sortDirection: "ASC",
                property: "CUSTOM_FIELD" as any,
                customFieldId: customField.id,
              };
              setSort(newSort);
              setFilters((prev) => ({
                ...prev,
                sortBy: newSort,
              }));
            }
          } else {
            setSort([
              ...sort,
              {
                headerId: id,
                sortDirection: "ASC",
                property: "CUSTOM_FIELD" as any,
                customFieldId: customField.id,
              },
            ]);
            setFilters((prev) => ({
              ...prev,
              ...(Array.isArray(prev.sortBy) && !!prev.sortBy.length
                ? {
                    sortBy: [
                      ...prev.sortBy,
                      { property: "CUSTOM_FIELD", direction: "ASC", customFieldId: customField.id },
                    ],
                  }
                : { sortBy: [{ property: "CUSTOM_FIELD", direction: "ASC", customFieldId: customField.id }] }),
            }));
          }
        },
        sortDirection: (id) => {
          const currentSortIndex =
            Array.isArray(sort) && !!sort.length ? sort.findIndex((s) => s.headerId === id) : null;
          if (currentSortIndex !== null && currentSortIndex !== undefined && currentSortIndex >= 0) {
            return sort[currentSortIndex].sortDirection;
          }
          return undefined;
        },
        sortIndex: (id: string) => {
          if (Array.isArray(sort) && !!sort.length) {
            const currentSortIndex = sort.findIndex((s) => s.headerId === id);
            return currentSortIndex >= 0 ? currentSortIndex + 1 : undefined;
          }
          return undefined;
        },
      }),
    });
  });

  return res;
};

interface UseCustomTypesBulkEditUtilsProps<Entity extends GenericEntity & ICustomTypedEntity> {
  initItems: MultiEditMappedItemObject<Entity> | undefined;
}
export const useCustomTypesBulkEditUtils = <Entity extends GenericEntity & ICustomTypedEntity>({
  initItems,
}: UseCustomTypesBulkEditUtilsProps<Entity>) => {
  const [uniqueCustomTypeIds, setUniqueCustomTypeIds] = useState<Set<CustomType["id"]>>(new Set());
  useEffect(() => {
    setUniqueCustomTypeIds(
      new Set(
        Object.values(initItems ?? {})
          .map((i) => i.customType?.id)
          .filter((v) => v !== null)
      ) as Set<number>
    );
  }, [initItems]);

  const onCustomTypeSelectCallback = useCallback((customTypeId: CustomType["id"] | undefined) => {
    if (customTypeId !== undefined) {
      setUniqueCustomTypeIds((prev) => {
        const set = new Set(Array.from(prev));
        if (set.has(customTypeId)) {
          set.delete(customTypeId);
        } else {
          set.add(customTypeId);
        }
        return set;
      });
    }
  }, []);

  return { uniqueCustomTypeIds, onCustomTypeSelectCallback };
};

export const canSortByCustomField = (dataType: CustomField["dataType"]): boolean => {
  if (CustomFieldDataTypeUtils.IsSingleEntityReference(dataType)) return true;
  switch (dataType) {
    case "String":
    case "Integer":
    case "Float":
    case "Boolean":
    case "Date":
    case "DateTime":
    case "Time":
    case "TimeRange":
    case "Url":
    case "Barcode":
      return true;
    default:
      return false;
  }
};
